/**
 * State Classification Model (SCM) - Complete Enhanced Entry
 * Exemplar of Type 2 (Clarity & Alignment) framework depth
 * 
 * Core Question: "What kind of knob are you holding before you write rules for how to turn it?"
 */

import type { EnhancedFrameworkEntry } from '../../types/enhancedFramework';

export const stateClassificationModel: EnhancedFrameworkEntry = {
  // ===== BASIC IDENTITY =====
  id: 'state-classification-model',
  canonicalName: 'State Classification Model',
  aliases: ['SCM', 'State Topology', 'Modality Framework', 'Control Surface Model'],
  
  // ===== TYPE CLASSIFICATION =====
  frameworkType: 'clarity-alignment',
  
  // ===== MULTI-RESOLUTION CONTENT =====
  content: {
    phrase: 'What kind of knob are you holding before you write rules for how to turn it?',
    
    sentence: 'The State Classification Model names the form of variation a property can take—Toggle, Dial, Multi-State, or Nested System—so we can describe reality with precision, govern it with rules, and evaluate outcomes coherently.',
    
    paragraph: 'The State Classification Model (SCM) sits between ontology (what exists) and dynamics (how things change), providing the "geometry of state" on which policies, thresholds, and transitions operate. It reduces category mistakes (e.g., treating a continuum like a binary), improves communication and decision-making, and supplies a reusable language that scales from individuals to organizations. SCM specifies the shape of the variable (the control surface); rules specify how you move within that surface.',
    
    fullPage: `# State Classification Model (SCM)
## Canonical Synthesis v1.0

## The Core Question

**"What kind of knob are you holding before you write rules for how to turn it?"**

## Introduction & Purpose

The State Classification Model (SCM) names the form of variation a property can take so we can describe reality with precision, govern it with rules, and evaluate outcomes coherently. It sits between ontology (what exists) and dynamics (how things change), providing the "geometry of state" on which policies, thresholds, and transitions operate.

SCM's aim is practical clarity. It reduces category mistakes (e.g., treating a continuum like a binary), improves communication and decision-making, and supplies a reusable language that scales from individuals to organizations.

## Conceptual Overview: The Four-Layer Stack

SCM belongs to a four-layer meta-model for representing and steering reality:

1. **Ontology (Meta-Taxonomy)** — what kinds of things exist (nouns)
2. **Modality / State Topology (SCM)** — how attributes vary (adjectives: toggle/dial/multi-state/nested)
3. **Dynamics (Rules & Integration)** — how states evolve under conditions (verbs)
4. **Evaluation (Feedback & Value)** — how we know if it worked (epistemic layer)

**Essential distinction**: SCM specifies the shape of the variable (the control surface); rules specify how you move within that surface (or constrain movement).

## Core Components of the Model

### 1) Toggle
**Definition**: A binary state (on/off; include/exclude).

**Implication for rules**: Inclusion/exclusion policies, hard thresholds, reversible vs. irreversible flips.

**Examples**: Light switch, feature flag, yes/no decision, go/no-go gate

**Leadership implications**: Use when costs are discontinuous or when windows collapse. Guardrails: specify reversibility, lock-out conditions, and re-entry criteria.

### 2) Dial (Range / Continuum)
**Definition**: A continuous variable adjustable within limits.

**Implication for rules**: Weighting, operating bands, drift control, target zones. Thresholds can make a dial behave like a toggle.

**Examples**: Temperature (0-100°C), intensity (0-10), quality scale (1-5), confidence level

**Leadership implications**: Adjustable range with thresholds; supports monitoring, optimization, and gradual commitment. Often discretized into Bands for fast, consistent action at scale.

### 3) Multi-State (Modes)
**Definition**: A finite set of discrete modes.

**Implication for rules**: Mode selection/rotation, entry/exit criteria per mode.

**Examples**: Status (Not Started/In Progress/Complete), role types, phases, STARS contexts

**Leadership implications**: Finite, named modes with entry/exit gates (e.g., Explore/Exploit, Build/Run). Leadership work: define mode promises, switching costs, and rotation cadence.

### 4) Nested System (Coupled Dimensions)
**Definition**: Interdependent sub-variables that must be coordinated (e.g., precision × expressiveness).

**Implication for rules**: Coupling constraints, coordination policies, tradeoffs across axes.

**Examples**: Color (hue + saturation + lightness), location (lat + long + alt), team performance (quality + speed + morale)

**Leadership implications**: Interdependent dimensions that must be coordinated. Govern with coupling constraints and trade-off rules, not single-axis targets.

## Enriching Constructs

### A) Range/Contour Bands (Discretized overlays on a Dial)

**Purpose**: Name selectable "slices" (bands) of a continuum bounded by thresholds to speed judgment and bind action policies.

**Design choices**: 
- Cardinality (3–5 bands)
- Hard vs. soft thresholds
- Uniform vs. criterion-based vs. adaptive spacing
- Monotonic label semantics
- Default actions (owner/SLA/rituals/escalations)

**Canonical band packs**: 
- Intensity (None→Extreme)
- Sufficiency (Insufficient→Fully Sufficient)
- Risk (Low→High)
- Confidence (Low→High)
- Maturity (Dreyfus-aligned)

**Minimal schema & selector UX**: thresholds T0–T4; interval bindings; tooltips; metric binding with manual override (w/ reason)

**Calibration & governance**: write band definitions w/ exemplars & misclassification costs; review thresholds when forces shift; log overrides

**Failure modes**: too many bands; misleading labels; over-reliance on color; mixing constructs on one axis

**Naming**: Range Bands / Contour Bands (thresholds as contours)

### B) Liminal States & Temporal Windows

Liminal zones are "in-between" periods with heightened consequence. Two temporal window archetypes augment SCM:

**Collapsing windows**: inaction closes the option (forces binary act/lose)
**Sliding windows**: inaction pushes/pulls the desired state in time (stretches the dial)

These windows are shaped by forces & triggers (urgency, complexity, reversibility, external shocks) and add explicit time-dependence to state choice and rule design. Consider ephemeral states (exist only within a window) and oscillating windows (recur).

### C) Tolerant Precision & Qualitative Primacy (States and Overlays)

**Tolerant Precision**: Appears numerically exact, but outcomes are robust within broad tolerances; craft/judgment > decimals.

**Qualitative Primacy State**: Countables exist, but value/performance is fundamentally qualitative (taste, feel, emergent property).

These can be treated as named states in SCM or as cautionary overlays reminding us where metrics are secondary.

### D) Precision Pitfalls (Meta-States)

**Context-Blind Precision**: False confidence from applying precise instruction without contextual fit (e.g., sea-level recipes at altitude).

**Pointless Precision (Von Neumann)**: Exactness applied before conceptual clarity; numbers masquerading as wisdom.

Treat both as cross-cutting meta-states that can overlay any SCM component to signal epistemic risk.

## Interrelationships & Dynamics

### State Shifts & Triggers
Thresholds convert dials to toggles; mode gates regulate multi-state entry/exit; coupling rules coordinate nested systems.

Forces/Triggers (urgency, reversibility, external shocks) shape window behavior and thus state feasibility.

### Threshold Concepts & Bands
Bands articulate named intervals between thresholds on a continuum and bind default actions to each band (policy + owner + cadence + escalation). This converts analysis into operational ritual.

### Liminality & Temporality
Liminal zones + time-bounded windows explain why hesitating destroys options vs. why waiting strengthens positioning, giving leaders language for "act now" vs. "wait" guidance.

## Application Across Levels

### Individuals (craft, coaching, mastery)
Use Tolerant Precision and Qualitative Primacy to avoid metric myopia in craft and leadership presence; rely on felt sense where it truly governs value.

Apply banded dials for self-assessment (e.g., Confidence Pack) with clear actions per band.

### Teams (execution, decision rituals)
Declare whether a dimension is toggle, dial, mode, or nested before writing rules.

Run window checks in planning: are we in a collapsing/sliding/oscillating window? What forces are present?

Use seven-column scaffolds (definition, properties, transition rules, common errors, implications, examples) to communicate states and reduce misalignment.

### Organizations (governance, portfolios)
Standardize band packs (e.g., Risk, Sufficiency, Maturity) and tune thresholds by context; bind each band to owners & SLAs.

Guard against context-blind and pointless precision in dashboards; elevate qualitative primacy domains explicitly.

## Integration with Peak Priorities & the Knowledge Engine

Within your Knowledge Engine, SCM is a sub-primitive under Structure/State (the "adjectives" layer). It bridges your ontology (categories, structures, relationships, instances, systems) and dynamics (integration, assembly, rules). Evaluation closes the loop.

To operationalize SCM in Peak Priorities rituals and tools:

- Embed band schemas in templates and UI controls (segmented selectors, tooltips, override+reason)
- Use the seven-column universal to document each state choice and communicate transition logic

## The Seven-Column State Dossier (for every SCM decision)

Use this "universal" structure to make every classification explicit and teachable:

1. **Category Name** - What state type is this?
2. **Definition** - Precise description of the state
3. **Properties** - Key characteristics and behaviors
4. **Transition Rules** - How states change and what triggers them
5. **Common Errors** - Typical mistakes and misclassifications
6. **Implications** - What this means for governance and action
7. **Examples** - Concrete illustrations

**Why**: avoids bad leadership advice, prevents misaligned expectations, and makes complex decisions repeatable.

## Resolving Open Questions & Ambiguities

**Are "Tolerant Precision" and "Qualitative Primacy" first-class states or overlays?**
Resolution: Treat them as states when they are the governing mode of a domain; also make them overlays to inoculate other states against metric misuse.

**Where do Collapsing/Sliding Windows live?**
Resolution: A temporal layer that applies across Toggles, Dials, Modes, and Nested Systems, and that can itself create ephemeral states.

**Bands vs. Raw Dials—when to discretize?**
Resolution: Use bands when you need judgment at speed and consistent action policies; maintain raw dials for monitoring/optimization. Calibrate bands by misclassification cost and shifting forces.

## Implementation Checklists

**Before rule-writing, state the state**: toggle/dial/mode/nested? (avoid category errors)

**If Dial, decide bands**: pick pack; set threshold types/spacing; bind default actions

**Run temporal scan**: any collapsing/sliding/oscillating windows? identify forces & triggers

**Apply precision overlays**: Is this domain tolerant? Is qualitative primacy in force? Beware context-blind or pointless precision

**Publish a State Dossier**: seven columns for communication & training

## Closing

SCM, positioned as Modality / State Topology in your four-layer stack, provides a rigorous, reusable substrate for sense-making and coordination. With Bands, Liminal Windows, and Precision Overlays, it becomes a navigational system that captures what kind of knob you're holding, when and why it must change, and how to act coherently at every level.`
  },
  
  // ===== RELEVANCE FRAMEWORK =====
  relevance: {
    what: 'A four-state classification system (Toggle, Dial, Multi-State, Nested System) that defines the "geometry of state" for any variable or property, enabling precise governance, rule-writing, and decision-making across all organizational levels.',
    
    why: 'Most governance failures stem from category mistakes—treating a continuum like a binary, or applying precise rules to qualitative domains. SCM prevents these errors by naming the state topology first, then binding appropriate rules to that topology. It reduces miscommunication, improves decision-making, and creates reusable language for sense-making.',
    
    how: 'By classifying every variable into one of four state types before writing rules, then applying appropriate governance patterns: Toggle (binary policies), Dial (thresholds and bands), Multi-State (mode gates), Nested System (coupling constraints). Enhanced with Range Bands for discretization, Temporal Windows for time-dependence, and Precision Overlays to avoid metric misuse.'
  },
  
  // ===== FRAMEWORK STRUCTURE =====
  structure: {
    components: [
      {
        name: 'Core State Types',
        description: 'The four fundamental forms of variation',
        purpose: 'Provide the basic taxonomy for classifying any variable'
      },
      {
        name: 'Range/Contour Bands',
        description: 'Discretized overlays on continuous variables',
        purpose: 'Enable fast judgment and consistent action policies'
      },
      {
        name: 'Temporal Windows',
        description: 'Time-dependent state behaviors',
        purpose: 'Handle collapsing, sliding, and oscillating windows'
      },
      {
        name: 'Precision Overlays',
        description: 'Meta-states for epistemic awareness',
        purpose: 'Prevent context-blind and pointless precision'
      },
      {
        name: 'State Dossier Template',
        description: 'Seven-column universal documentation format',
        purpose: 'Make every classification explicit and teachable'
      }
    ],
    questions: [
      {
        number: 1,
        question: 'What kind of knob are you holding?',
        intent: 'Classify the variable into one of four state types',
        guidance: 'Before writing any rules or policies, determine if this is a Toggle (binary), Dial (continuum), Multi-State (discrete modes), or Nested System (coupled dimensions). This classification determines all subsequent governance approaches.',
        examples: [
          '✓ "This is a Toggle—either we launch or we don\'t"',
          '✓ "This is a Dial—we can adjust confidence from 0-10"',
          '✓ "This is Multi-State—we\'re in Explore mode, not Exploit"',
          '✓ "This is Nested—quality and speed are coupled"',
          '✗ "This is important" (not a state classification)'
        ]
      },
      {
        number: 2,
        question: 'If it\'s a Dial, do you need Bands?',
        intent: 'Determine if discretization is needed for operational consistency',
        guidance: 'Use bands when you need judgment at speed and consistent action policies. Choose 3-5 bands with clear thresholds, bind each band to default actions, owners, and SLAs. Maintain raw dials for monitoring and optimization.',
        examples: [
          '✓ "Risk: Low/Medium/High with clear escalation rules"',
          '✓ "Confidence: Insufficient/Sufficient/Target/Ideal with actions"',
          '✗ "Everything on a 1-10 scale" (too granular)',
          '✗ "Just monitor the raw number" (no action guidance)'
        ]
      },
      {
        number: 3,
        question: 'Are there Temporal Windows?',
        intent: 'Identify time-dependent state behaviors',
        guidance: 'Check for collapsing windows (act or lose), sliding windows (timeline stretches), or oscillating windows (recurring). Identify the forces and triggers shaping these windows.',
        examples: [
          '✓ "Collapsing window: market opportunity closes in 30 days"',
          '✓ "Sliding window: deadline can extend but costs increase"',
          '✓ "Oscillating window: quarterly budget cycles"',
          '✗ "We have time" (not specific about window type)'
        ]
      },
      {
        number: 4,
        question: 'What Precision Overlays apply?',
        intent: 'Determine if this domain requires special epistemic awareness',
        guidance: 'Apply Tolerant Precision for craft domains where judgment matters more than decimals. Apply Qualitative Primacy where felt sense governs value. Guard against Context-Blind Precision (precision without context) and Pointless Precision (exactness before clarity).',
        examples: [
          '✓ "Tolerant Precision: leadership presence, team chemistry"',
          '✓ "Qualitative Primacy: user experience, brand perception"',
          '✓ "Guard against: dashboard metrics without context"',
          '✗ "Everything needs precise measurement" (precision theater)'
        ]
      },
      {
        number: 5,
        question: 'What\'s your State Dossier?',
        intent: 'Document the classification using the seven-column template',
        guidance: 'Create a State Dossier with: Category Name, Definition, Properties, Transition Rules, Common Errors, Implications, Examples. This makes the classification explicit, teachable, and auditable.',
        examples: [
          '✓ "Published State Dossier for Risk Classification"',
          '✓ "Team trained on Confidence Band definitions"',
          '✓ "Governance rules documented and accessible"',
          '✗ "We know what we mean" (not documented)'
        ]
      }
    ]
  },
  
  // ===== RELATIONSHIPS =====
  relationships: {
    precededBy: [
      {
        frameworkId: 'meta-taxonomy-framework',
        description: 'SCM operates within the Ontology layer, classifying the "adjectives" (how properties vary) that complement the "nouns" (what exists) defined by the Meta-Taxonomy Framework.'
      }
    ],
    followedBy: [
      {
        frameworkId: 'rules-engine',
        description: 'Once state topology is classified, rules engine specifies how to move within or between states, implementing the dynamics layer of the four-layer stack.'
      }
    ],
    partOf: [
      {
        frameworkId: 'four-layer-meta-stack',
        relationshipType: 'implements',
        description: 'SCM implements the Modality/State Topology layer of the four-layer meta-stack, bridging Ontology and Dynamics.'
      }
    ],
    relatedTo: [
      {
        frameworkId: 'desired-outcome-framework',
        relationshipType: 'extends',
        description: 'The Evidence range (Barely Sufficient → Target → Hopelessly Idealistic) in DOF is a direct application of SCM\'s Dial concept with Range Bands.'
      },
      {
        frameworkId: 'stars-contexts',
        relationshipType: 'exemplifies',
        description: 'STARS contexts (Stable, Turbulent, Accelerating, Realigning, Startup) are a Multi-State classification of organizational contexts.'
      },
      {
        frameworkId: 'degap-stages',
        relationshipType: 'exemplifies',
        description: 'DEGAP stages represent a Multi-State classification of prioritization phases with entry/exit gates.'
      }
    ],
    usedIn: [
      {
        frameworkId: 'assessment-framework',
        stage: 'Pattern Detection',
        purpose: 'SCM informs how assessment patterns are classified and how thresholds between score bands are defined and applied.'
      },
      {
        frameworkId: 'governance-systems',
        stage: 'Policy Design',
        purpose: 'All governance policies should first classify the state topology of variables they govern, then apply appropriate rule patterns.'
      }
    ]
  },
  
  // ===== SCOPE & APPLICABILITY =====
  scope: {
    levels: ['individual', 'team', 'organization', 'ecosystem'],
    contexts: [
      'Designing governance systems and policies',
      'Classifying variables before writing rules',
      'Preventing category mistakes in decision-making',
      'Creating consistent action frameworks',
      'Managing organizational rhythms and rituals',
      'Designing assessment and evaluation systems',
      'Planning temporal windows and deadlines',
      'Avoiding precision theater and metric misuse',
      'Creating shared language for state management',
      'Any situation requiring precise variable classification'
    ],
    stages: [
      {
        stage: 'Before rule-writing',
        purpose: 'Classify state topology to determine appropriate governance patterns'
      },
      {
        stage: 'During system design',
        purpose: 'Apply SCM to ensure consistent state management across all variables'
      },
      {
        stage: 'At review/audit',
        purpose: 'Validate that state classifications remain accurate and governance patterns are appropriate'
      }
    ]
  },
  
  // ===== PRACTICAL RESOURCES =====
  resources: {
    templates: [
      {
        id: 'state-dossier-template',
        name: 'Seven-Column State Dossier Template',
        type: 'worksheet',
        description: 'Universal template for documenting any state classification',
        content: `# State Dossier Template

## 1. Category Name
[Toggle/Dial/Multi-State/Nested System]

## 2. Definition
[Precise description of what this state type means]

## 3. Properties
[Key characteristics, behaviors, and constraints]

## 4. Transition Rules
[How states change, what triggers transitions, entry/exit criteria]

## 5. Common Errors
[Typical mistakes and misclassifications to avoid]

## 6. Implications
[What this means for governance, decision-making, and action]

## 7. Examples
[Concrete illustrations and use cases]`
      },
      {
        id: 'band-pack-template',
        name: 'Range Band Pack Template',
        type: 'worksheet',
        description: 'Template for creating discretized bands on continuous variables',
        content: `# Range Band Pack Template

## Band Pack: [Name]
**Variable**: [What continuous variable this applies to]
**Purpose**: [Why discretization is needed]

### Band Definitions
- **Band 1**: [Name] - [Threshold range] - [Default action/owner/SLA]
- **Band 2**: [Name] - [Threshold range] - [Default action/owner/SLA]
- **Band 3**: [Name] - [Threshold range] - [Default action/owner/SLA]

### Thresholds
- T0: [Lower bound]
- T1: [First threshold]
- T2: [Second threshold]
- T3: [Third threshold]
- T4: [Upper bound]

### Calibration Notes
[Exemplars, misclassification costs, review cadence]`
      }
    ],
    
    facilitationGuides: [
      {
        title: 'State Classification Workshop',
        duration: 90,
        roles: ['Facilitator', 'Domain Expert', 'Scribe', 'Stakeholders (≤8 people)'],
        materials: ['SCM Reference', 'State Dossier Template', 'Band Pack Template', 'Examples'],
        steps: [
          {
            stepNumber: 1,
            title: 'Frame the Problem',
            duration: 10,
            instructions: 'Explain SCM purpose: prevent category mistakes, create shared language, enable precise governance. Share examples of toggle vs dial mistakes.',
            tips: [
              'Use the "knob" metaphor throughout',
              'Share examples of governance failures due to wrong state classification'
            ]
          },
          {
            stepNumber: 2,
            title: 'Inventory Variables',
            duration: 20,
            instructions: 'List all variables, properties, or dimensions that need governance. Don\'t classify yet—just inventory.',
            tips: [
              'Include both explicit and implicit variables',
              'Consider process, people, technology, policy dimensions'
            ]
          },
          {
            stepNumber: 3,
            title: 'Classify State Types',
            duration: 30,
            instructions: 'For each variable, determine: Toggle, Dial, Multi-State, or Nested System. Use the core question: "What kind of knob is this?"',
            tips: [
              'Start with obvious ones to build confidence',
              'Debate edge cases—they\'re often the most important',
              'Document reasoning for each classification'
            ]
          },
          {
            stepNumber: 4,
            title: 'Design Bands (for Dials)',
            duration: 20,
            instructions: 'For each Dial, determine if bands are needed. If yes, design 3-5 bands with thresholds, actions, owners, SLAs.',
            tips: [
              'Use canonical band packs when possible',
              'Bind each band to specific actions and accountability',
              'Consider misclassification costs'
            ]
          },
          {
            stepNumber: 5,
            title: 'Check for Windows',
            duration: 10,
            instructions: 'Identify any temporal windows: collapsing, sliding, or oscillating. Note forces and triggers.',
            tips: [
              'Look for time-dependent behaviors',
              'Consider external forces and triggers',
              'Plan for ephemeral states'
            ]
          },
          {
            stepNumber: 6,
            title: 'Apply Precision Overlays',
            duration: 10,
            instructions: 'Determine which variables need Tolerant Precision, Qualitative Primacy, or precision guards.',
            tips: [
              'Identify craft domains where judgment matters',
              'Flag areas prone to precision theater',
              'Protect qualitative primacy domains'
            ]
          },
          {
            stepNumber: 7,
            title: 'Create State Dossiers',
            duration: 20,
            instructions: 'Document key classifications using the seven-column template. Focus on the most important or controversial ones.',
            tips: [
              'Start with high-impact variables',
              'Include examples and common errors',
              'Make them teachable and auditable'
            ]
          },
          {
            stepNumber: 8,
            title: 'Plan Implementation',
            duration: 10,
            instructions: 'Assign owners, set review cadence, plan training, integrate into existing systems.',
            tips: [
              'Start with pilot applications',
              'Plan training for key stakeholders',
              'Schedule regular review and calibration'
            ]
          }
        ],
        commonPitfalls: [
          'Classifying everything as a Dial to avoid binary decisions',
          'Creating too many bands (keep to 3-5)',
          'Missing temporal windows and their implications',
          'Applying precision overlays without understanding the domain',
          'Not documenting classifications in State Dossiers',
          'Failing to bind bands to specific actions and accountability'
        ]
      }
    ],
    
    instructions: {
      individual: '# Using SCM Solo\n\n1. **Identify a variable** you need to govern or make decisions about\n2. **Ask the core question**: "What kind of knob am I holding?"\n3. **Classify**: Toggle, Dial, Multi-State, or Nested System\n4. **If Dial**: Determine if you need bands for operational consistency\n5. **Check for windows**: Are there temporal constraints or forces?\n6. **Apply precision overlays**: Is this tolerant precision, qualitative primacy, or needs precision guards?\n7. **Document**: Create a simple State Dossier for important classifications\n8. **Use consistently**: Apply the same classification logic to related variables\n\n**Tip**: Start with personal decisions (career, health, relationships) to build intuition before applying to organizational contexts.',
      
      team: '# Using SCM with a Team\n\n**Roles needed:**\n- **Facilitator** (guides process, keeps time)\n- **Domain Expert** (understands the variables being classified)\n- **Scribe** (captures classifications and reasoning)\n- **Stakeholders** (≤8 people with perspective on the variables)\n\n**Run time:** 90 minutes\n\n**Process:** Follow the facilitation guide steps 1-8\n\n**Key success factors:**\n- Pre-read: Share SCM reference and examples beforehand\n- Start simple: Begin with obvious classifications to build confidence\n- Document reasoning: Capture why each classification was chosen\n- Focus on edge cases: These are often the most important\n- Create State Dossiers: Make classifications teachable and auditable\n- Plan implementation: Assign owners and review cadence\n\n**After the session:**\n- Type up State Dossiers within 24 hours\n- Circulate for review and refinement\n- Integrate into existing governance systems\n- Schedule regular review and calibration\n\n**When NOT to use:**\n- Don\'t over-classify simple, obvious variables\n- Don\'t create precision theater where qualitative judgment is more appropriate\n- Don\'t force everything into SCM—some variables are better left ungoverned'
    },
    
    criticalCallouts: [
      {
        id: 'core-question',
        type: 'tip',
        title: 'The Core Question',
        content: 'Always ask: "What kind of knob are you holding before you write rules for how to turn it?" This prevents category mistakes and ensures appropriate governance patterns.',
        icon: '🎛️'
      },
      {
        id: 'category-mistakes',
        type: 'warning',
        title: 'Category Mistakes Are Expensive',
        content: 'Treating a continuum like a binary (or vice versa) drives brittle policies and "false clarity." SCM prevents this by naming the state topology first.',
        icon: '⚠️'
      },
      {
        id: 'four-layer-stack',
        type: 'requirement',
        title: 'SCM in the Four-Layer Stack',
        content: 'SCM sits between Ontology (what exists) and Dynamics (how things change), providing the "geometry of state" on which policies operate.',
        icon: '🏗️'
      },
      {
        id: 'bands-vs-raw',
        type: 'tip',
        title: 'Bands vs. Raw Dials',
        content: 'Use bands when you need judgment at speed and consistent action policies. Maintain raw dials for monitoring and optimization. Calibrate bands by misclassification cost.',
        icon: '📊'
      },
      {
        id: 'temporal-windows',
        type: 'requirement',
        title: 'Check for Temporal Windows',
        content: 'Look for collapsing windows (act or lose), sliding windows (timeline stretches), or oscillating windows (recurring). These change how states behave over time.',
        icon: '⏰'
      },
      {
        id: 'precision-overlays',
        type: 'warning',
        title: 'Apply Precision Overlays',
        content: 'Guard against Context-Blind Precision (precision without context) and Pointless Precision (exactness before clarity). Apply Tolerant Precision and Qualitative Primacy where appropriate.',
        icon: '🎯'
      },
      {
        id: 'state-dossier',
        type: 'requirement',
        title: 'Document with State Dossiers',
        content: 'Use the seven-column template (Category, Definition, Properties, Transition Rules, Common Errors, Implications, Examples) to make every classification explicit and teachable.',
        icon: '📋'
      },
      {
        id: 'canonical-bands',
        type: 'tip',
        title: 'Use Canonical Band Packs',
        content: 'Leverage proven band packs: Intensity, Sufficiency, Risk, Confidence, Maturity. These have been calibrated and tested across contexts.',
        icon: '📦'
      },
      {
        id: 'liminal-states',
        type: 'tip',
        title: 'Recognize Liminal States',
        content: 'Liminal zones are "in-between" periods with heightened consequence. They require special attention and often create ephemeral states that exist only within windows.',
        icon: '🌅'
      },
      {
        id: 'implementation-checklist',
        type: 'requirement',
        title: 'Follow Implementation Checklist',
        content: 'Before rule-writing: state the state → decide bands → run temporal scan → apply precision overlays → publish State Dossier.',
        icon: '✅'
      }
    ],
    
    media: []
  },
  
  // ===== LEARNING INTEGRATION =====
  learning: {
    frameworkType: 'clarity-alignment',
    difficulty: 3,
    timeToLearn: 45,
    prerequisites: [
      {
        frameworkId: 'meta-taxonomy-framework',
        reason: 'Understanding the four-layer stack helps place SCM in context'
      },
      {
        frameworkId: 'relevance-framework',
        reason: 'SCM itself is explained using What/Why/How structure'
      }
    ],
    practiceExercises: [
      {
        title: 'Personal Variable Classification',
        description: 'Classify personal variables (health, career, relationships) using SCM. Practice all four state types.',
        duration: 30,
        difficulty: 2
      },
      {
        title: 'Team Process Mapping',
        description: 'Map a team process and classify each variable. Design bands for continuous variables.',
        duration: 45,
        difficulty: 3
      },
      {
        title: 'Governance System Audit',
        description: 'Audit existing governance systems using SCM. Identify category mistakes and misclassifications.',
        duration: 60,
        difficulty: 4
      }
    ]
  },
  
  // ===== VISUAL LANGUAGE =====
  visual: {
    primaryDiagram: {
      type: 'taxonomy',
      title: 'The Four State Types',
      description: 'Visual representation of the four core state types: Toggle (binary switch), Dial (continuous slider), Multi-State (discrete modes), and Nested System (coupled dimensions).',
      svgComponent: 'StateTypesDiagram',
      interactive: true,
      altText: 'Four-panel diagram showing Toggle (on/off switch), Dial (slider with bands), Multi-State (mode selector), and Nested System (coupled grid)'
    },
    supportingDiagrams: [
      {
        type: 'continuum',
        title: 'Range Bands on a Dial',
        description: 'Shows how continuous variables can be discretized into named bands with thresholds, actions, and owners.',
        svgComponent: 'RangeBandsDiagram',
        interactive: true,
        altText: 'Horizontal slider with labeled bands and thresholds, showing actions and owners for each band'
      },
      {
        type: 'timeline',
        title: 'Temporal Windows',
        description: 'Illustrates collapsing, sliding, and oscillating windows over time, showing how they affect state behavior.',
        svgComponent: 'TemporalWindowsDiagram',
        interactive: false,
        altText: 'Timeline showing different window types and their effects on state transitions'
      },
      {
        type: 'matrix',
        title: 'Precision Overlays Matrix',
        description: 'Shows when to apply Tolerant Precision, Qualitative Primacy, and precision guards across different domains.',
        svgComponent: 'PrecisionOverlaysDiagram',
        interactive: false,
        altText: 'Matrix showing precision types mapped to different organizational domains'
      }
    ],
    icon: '🎛️',
    color: '#8cb299',  // sage-500
    metaphor: {
      family: 'control',
      phrase: 'Understanding the control surface before designing the controls',
      visualCues: ['🎛️ Control Panel', '🔘 Toggle Switch', '🎚️ Slider Dial', '📊 Band Ranges', '⏰ Temporal Windows']
    },
    onBrandElements: {
      backgroundColor: '#f4f7f5',  // sage-50
      accentColor: '#8cb299',      // sage-500
      borderStyle: '2px solid #bad1c2',  // sage-300
      shadowStyle: '0 4px 6px rgba(140, 178, 153, 0.1)'
    }
  },
  
  // ===== QUALITY & MISSTEPS =====
  quality: {
    checklist: {
      items: [
        {
          criterion: 'Core question asked: "What kind of knob are you holding?"',
          passFail: true,
          guidance: 'Did you classify the variable before writing rules?'
        },
        {
          criterion: 'State type correctly identified',
          passFail: true,
          guidance: 'Is this clearly Toggle, Dial, Multi-State, or Nested System?'
        },
        {
          criterion: 'Bands designed for Dials (if needed)',
          passFail: true,
          guidance: 'Are there 3-5 bands with clear thresholds, actions, and owners?'
        },
        {
          criterion: 'Temporal windows identified',
          passFail: true,
          guidance: 'Are there collapsing, sliding, or oscillating windows?'
        },
        {
          criterion: 'Precision overlays applied',
          passFail: true,
          guidance: 'Are Tolerant Precision, Qualitative Primacy, or precision guards appropriate?'
        },
        {
          criterion: 'State Dossier created',
          passFail: true,
          guidance: 'Is the classification documented using the seven-column template?'
        },
        {
          criterion: 'Implementation planned',
          passFail: true,
          guidance: 'Are owners assigned, review cadence set, and training planned?'
        }
      ]
    },
    commonMissteps: {
      missteps: [
        {
          mistake: 'Skipping state classification',
          consequence: 'Write inappropriate rules, create category mistakes, governance failures',
          fix: 'Always ask "What kind of knob are you holding?" before writing any rules or policies',
          relatedQuestion: 'Core question: State type classification'
        },
        {
          mistake: 'Treating everything as a Dial',
          consequence: 'Avoid necessary binary decisions, create false complexity, miss clear choices',
          fix: 'Recognize when variables are truly binary (Toggle) or discrete (Multi-State)',
          relatedQuestion: 'Question 1: State type classification'
        },
        {
          mistake: 'Creating too many bands',
          consequence: 'Decision paralysis, inconsistent application, over-complexity',
          fix: 'Limit to 3-5 bands maximum. Use canonical band packs when possible',
          relatedQuestion: 'Question 2: Band design'
        },
        {
          mistake: 'Ignoring temporal windows',
          consequence: 'Miss time-dependent behaviors, make decisions at wrong times, fail to act when needed',
          fix: 'Always check for collapsing, sliding, or oscillating windows and their forces',
          relatedQuestion: 'Question 3: Temporal windows'
        },
        {
          mistake: 'Applying precision overlays incorrectly',
          consequence: 'Precision theater, metric misuse, context-blind decisions',
          fix: 'Apply Tolerant Precision to craft domains, Qualitative Primacy to felt-sense domains, guard against precision pitfalls',
          relatedQuestion: 'Question 4: Precision overlays'
        },
        {
          mistake: 'Not documenting classifications',
          consequence: 'Inconsistent application, lost reasoning, difficult to teach or audit',
          fix: 'Create State Dossiers for important classifications using the seven-column template',
          relatedQuestion: 'Question 5: State Dossier'
        },
        {
          mistake: 'Not binding bands to actions',
          consequence: 'Bands become decorative, no operational value, inconsistent responses',
          fix: 'Each band must have default actions, owners, SLAs, and escalation rules',
          relatedQuestion: 'Question 2: Band design'
        },
        {
          mistake: 'Missing liminal states',
          consequence: 'Miss critical transition periods, fail to recognize heightened consequence zones',
          fix: 'Identify in-between periods where state behavior changes significantly',
          relatedQuestion: 'Question 3: Temporal windows'
        },
        {
          mistake: 'Over-classifying simple variables',
          consequence: 'Unnecessary complexity, precision theater, governance overhead',
          fix: 'Only classify variables that need governance. Some things are better left ungoverned',
          relatedQuestion: 'All questions'
        },
        {
          mistake: 'Not reviewing classifications over time',
          consequence: 'Classifications become outdated, inappropriate governance patterns persist',
          fix: 'Schedule regular review and calibration, especially when forces shift',
          relatedQuestion: 'Implementation planning'
        }
      ]
    }
  },
  
  // ===== METADATA =====
  metadata: {
    createdDate: '2025-01-01',
    lastUpdated: '2025-01-29',
    version: '1.0',
    status: 'published',
    contributors: ['Peak Priorities Team', 'Systems Thinking Community'],
    tags: [
      'state-classification',
      'governance',
      'decision-making',
      'modality',
      'control-systems',
      'type-2',
      'clarity-alignment',
      'meta-model',
      'four-layer-stack',
      'precision',
      'temporal-windows',
      'range-bands',
      'liminal-states'
    ]
  }
};
